import { useState } from "react";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export const ImportModal = ({ groups, onClose, onDone }) => {
    const [file, setFile] = useState(null);
    const [text, setText] = useState("");
    const [groupIds, setGroupIds] = useState([]);
    const [consent, setConsent] = useState(false);
    const [busy, setBusy] = useState(false);
    const real = groups.filter((g) => !g.system);

    const submit = async () => {
        setBusy(true);
        try {
            const fd = new FormData();
            if (file) fd.append("file", file);
            fd.append("text", text);
            fd.append("group_ids", groupIds.join(","));
            fd.append("consent", consent ? "true" : "false");
            const { data } = await api.post("/mailing/recipients/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`Dodano ${data.added}, zaktualizowano ${data.updated}, pominięto błędnych: ${data.invalid}.`);
            onDone();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
            <div className="bg-white rounded-3xl shadow-2xl max-w-xl w-full p-7 my-8 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="mailing-import-modal">
                <div className="flex items-center justify-between">
                    <h3 className="font-serif text-2xl text-[#092E22]">Import adresów</h3>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={18} /></button>
                </div>
                <p className="text-xs text-gray-500">Plik CSV (separator „,” lub „;”) z kolumną <code>email</code> i opcjonalnie <code>imie</code>/<code>name</code>. Możesz też wkleić adresy – jeden w linii. Duplikaty są scalane.</p>
                <div className="space-y-2">
                    <Label>Plik CSV</Label>
                    <input type="file" accept=".csv,.txt" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="mailing-import-file" className="block w-full text-sm file:mr-3 file:px-4 file:py-2 file:rounded-full file:border-0 file:bg-[#0D4F3C]/10 file:text-[#0D4F3C]" />
                </div>
                <div className="space-y-2">
                    <Label>…lub wklej adresy</Label>
                    <Textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder={"jan@example.com;Jan Kowalski\nanna@example.com"} data-testid="mailing-import-text" />
                </div>
                <div className="space-y-2">
                    <Label>Dodaj do grup</Label>
                    <div className="flex flex-wrap gap-2">
                        {real.length === 0 && <p className="text-xs text-gray-400">Najpierw utwórz grupę.</p>}
                        {real.map((g) => (
                            <button type="button" key={g.id} data-testid={`mailing-import-group-${g.id}`} onClick={() => setGroupIds(groupIds.includes(g.id) ? groupIds.filter((x) => x !== g.id) : [...groupIds, g.id])}
                                className={`px-3 py-1 rounded-full border text-sm ${groupIds.includes(g.id) ? "bg-[#0D4F3C] text-white border-[#0D4F3C]" : "border-gray-200"}`}>{g.name}</button>
                        ))}
                    </div>
                </div>
                <label className="flex items-start gap-2 text-xs text-gray-700 bg-amber-50 border border-amber-200 rounded-xl p-3 cursor-pointer">
                    <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-[#0D4F3C]" data-testid="mailing-import-consent" />
                    Potwierdzam, że adresaci są członkami/mieszkańcami wspólnoty i wyrazili zgodę na kontakt e-mail (RODO). Każda wiadomość zawiera link do wypisania się.
                </label>
                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose} className="rounded-full">Anuluj</Button>
                    <Button onClick={submit} disabled={busy || !consent || (!file && !text.trim())} className="rounded-full bg-[#0D4F3C] hover:bg-[#092E22] text-white" data-testid="mailing-import-submit">
                        <Upload size={14} className="mr-2" /> {busy ? "Importowanie…" : "Importuj"}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ImportModal;
